import React, { useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutMePage: React.FC = () => {
  useEffect(() => {
    document.title = 'About | Kevyn Franco';
  }, []);

  return (
    <Box>
      <Header />
      
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", "Arial Black", sans-serif',
              textAlign: 'center',
              mb: 4,
              lineHeight: 1.1,
            }}
          >
            Kevyn Franco's Engineering Journey: From Code to AI-Powered Innovation
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8,
              color: '#333',
            }}
          >
            I'm an AI Architect & Senior Software Engineer. I design and build production AI systems for startups — RAG pipelines, LLM integrations, and intelligent agents using FastAPI, LangChain, and cloud infrastructure.
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              mt: 3,
              lineHeight: 1.8,
              color: '#333',
            }}
          >
            My journey of engineering innovation has been transformative. I'm grateful I followed my passion for technology.
          </Typography>
        </Container>
      </Box>

      {/* As Featured In Section */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: '#999',
              mb: 4,
              fontSize: { xs: '0.875rem', md: '1rem' },
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            As Featured In
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
              opacity: 0.4,
            }}
          >
            {['TechCrunch', 'Forbes', 'Wired', 'VentureBeat', 'The Next Web'].map((publication) => (
              <Typography
                key={publication}
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#666',
                  fontFamily: 'Arial, sans-serif',
                }}
              >
                {publication}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Story Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: '#999',
              mb: 6,
              fontSize: { xs: '0.875rem', md: '1rem' },
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            Here's my story
          </Typography>

          {/* Section 1 */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", "Arial Black", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              Finding My Path: Computer Science {'>'} Enterprise {'>'} AI Innovation
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              Back in university, I was fascinated by algorithms and data structures. But as someone who wanted to build products that people actually use, I quickly realized pure theory wasn't enough for me.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              I discovered that my passions were in creating. I was interested in full-stack development. I was interested in solving real problems.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              So I ended up working at enterprise companies, where I started building scalable systems. After several years there, I found my next passion: artificial intelligence and machine learning.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333',
              }}
            >
              Seeing the potential of AI-powered products was fascinating. To see the transformation that was possible—we hadn't seen anything like that since the mobile revolution.
            </Typography>
          </Box>

          {/* Section 2 */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              And that potential pulled my career in this entirely new direction.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              I left my comfortable enterprise position to dive deep into AI and machine learning. I was a product engineer, and I wanted to build something innovative and impactful.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333',
              }}
            >
              I had no idea I would end up becoming an AI Architect & Senior Software Engineer specializing in AI-powered products and building platforms that help businesses scale.
            </Typography>
          </Box>

          {/* Section 3 */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", "Arial Black", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              The AI Revolution That Transformed My Engineering Career
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              When I first started working with AI and machine learning, I realized that the traditional software development approach wasn't enough. Building intelligent systems required a different mindset—one that combined software engineering excellence with data science principles.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              I spent countless hours learning about neural networks, natural language processing, and computer vision. But more importantly, I learned how to integrate these technologies into production systems that could scale to millions of users.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333',
              }}
            >
              That's when everything changed. I started building AI-powered platforms that could automate complex workflows, provide intelligent recommendations, and deliver personalized experiences at scale.
            </Typography>
          </Box>

          {/* Section 4 */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", "Arial Black", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              Building Products That Scale: From Concept to Production
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              My approach to software engineering is simple: build systems that are robust, scalable, and maintainable. Whether it's designing microservices architectures, implementing CI/CD pipelines, or optimizing database performance, I focus on creating solutions that stand the test of time.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              I've worked with Python and JavaScript ecosystems extensively, building everything from RESTful APIs to real-time data processing pipelines. My experience spans across cloud platforms like AWS and Azure, containerization with Docker and Kubernetes, and modern frontend frameworks like React and Vue.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333',
              }}
            >
              But technology is just a tool. What matters most is understanding the business problem and delivering solutions that create real value for users and stakeholders.
            </Typography>
          </Box>

          {/* Section 5 */}
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", "Arial Black", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              What's Next: The Future of AI-Powered Engineering
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              Today, I'm focused on helping companies navigate the AI revolution. Whether it's building intelligent automation systems, implementing machine learning models in production, or architecting scalable cloud infrastructure, I'm passionate about pushing the boundaries of what's possible.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 3,
                color: '#333',
              }}
            >
              I believe we're at the beginning of a new era in software engineering—one where AI is not just a feature, but a fundamental part of how we build products. The engineers who can bridge the gap between traditional software development and AI will be the ones who shape the future.
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333',
              }}
            >
              If you're interested in learning more about how AI can transform your products, or if you want to discuss the latest trends in software engineering, feel free to reach out. I'm always excited to connect with fellow engineers and innovators.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default AboutMePage;